import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, fmtDate, GedPrepDisclaimer } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Calculator, BookText, FlaskConical, Landmark, Timer, Play, RotateCcw, BookOpen, Target, Loader2,
  Lock, CheckCircle2, Sparkles, RefreshCw, Clock, ExternalLink, Gift,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { math: Calculator, rla: BookText, science: FlaskConical, social: Landmark };
const BAND_TONE = {
  credit: "bg-[#DDF7E7] text-[#14663A]", college: "bg-[#DDF7E7] text-[#14663A]", pass: "bg-[#D7F3F1] text-[#0B5B5B]",
  close: "bg-[#FFF1C7] text-[#7A5A00]", practice: "bg-[#FFE1E1] text-[#9B2C2C]", none: "bg-secondary text-muted-foreground",
};

// 100-200 scale meter with the 145 passing line
export const ScoreMeter = ({ score }) => {
  const pct = score == null ? 0 : ((score - 100) / 100) * 100;
  return (
    <div className="relative h-2.5 rounded-full bg-[#EEF2F4] overflow-visible" aria-label={score ? `Estimated score ${score}` : "No estimate"}>
      <div className="absolute inset-y-0 left-0 rounded-full bg-[#0F7B7D]" style={{ width: `${pct}%` }} />
      <div className="absolute -top-1 w-0.5 bg-[#123645]" style={{ left: "45%", height: 18 }} title="145 passing" />
    </div>
  );
};

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, "")}`;

const checkout = async (product, { subject } = {}) => {
  try {
    const { data } = await api.post("/ged/checkout", {
      product,
      subject,
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const CompedBanner = ({ ent }) => {
  const exp = ent.comp_meta?.expires_at;
  const by = ent.comp_meta?.granted_by_name;
  return (
    <div className="bg-white border border-[#1F9D55]/30 rounded-[18px] p-5 sm:p-6 mb-6 flex flex-wrap items-center gap-4" data-testid="ged-comped-banner">
      <div className="h-12 w-12 rounded-xl bg-[#DDF7E7] text-[#14663A] flex items-center justify-center shrink-0"><Gift className="h-6 w-6" /></div>
      <div className="flex-1 min-w-[220px]">
        <div className="font-display font-semibold text-lg text-[#14663A]">GED Prep is on us</div>
        <div className="text-sm text-muted-foreground">
          You've been given <span className="font-medium text-[#071A22]">unlimited</span> access to the GED program and practice tests
          {exp ? ` through ${new Date(exp).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}` : ""}
          {by ? ` by ${by}` : ""}.
        </div>
      </div>
    </div>
  );
};

const Paywall = ({ price, busy, setBusy }) => (
  <div className="bg-white border border-border rounded-[20px] p-6 sm:p-8 mb-6" data-testid="ged-paywall">
    <div className="flex flex-wrap items-start gap-6">
      <div className="h-14 w-14 rounded-2xl bg-[#D7F3F1] text-[#0B6B6B] flex items-center justify-center shrink-0">
        <Lock className="h-7 w-7" />
      </div>
      <div className="flex-1 min-w-[260px]">
        <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">GED Prep is a separate program</div>
        <h2 className="mt-1 font-display font-semibold text-2xl text-[#071A22]">Unlock your 12-month GED Prep plan</h2>
        <p className="mt-2 text-[15px] text-muted-foreground max-w-xl">
          Get a full year of GED-aligned Math, RLA, Science and Social Studies courses with your AI tutor Sage.
          Practice tests are an optional add-on.
        </p>
        <ul className="mt-3 text-sm space-y-1.5">
          {[
            "4 standards-aligned courses, 48 lessons",
            "AI tutor Sage answers questions in your words",
            "Readiness score estimates on the GED 100-200 scale",
          ].map((x) => (
            <li key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#0F7B7D]" />{x}</li>
          ))}
        </ul>
      </div>
      <div className="w-full md:w-auto md:text-right">
        <div className="text-5xl font-display font-semibold text-[#071A22]" data-testid="ged-base-price">{money(price)}</div>
        <div className="text-xs text-muted-foreground">one-time · 12 months of access</div>
        <Button
          className="mt-4 h-11 w-full md:w-auto"
          onClick={async () => { setBusy("base"); await checkout("base"); setBusy(null); }}
          disabled={busy === "base"}
          data-testid="ged-unlock-base"
        >
          {busy === "base" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Unlock GED Prep
        </Button>
      </div>
    </div>
  </div>
);

const PackCard = ({ price, busy, setBusy }) => (
  <div className="bg-white border border-border rounded-[18px] p-5 sm:p-6 mb-6 flex flex-wrap items-center gap-5" data-testid="ged-pack-card">
    <div className="h-12 w-12 rounded-xl bg-[#FFF1C7] text-[#7A5A00] flex items-center justify-center shrink-0">
      <Sparkles className="h-6 w-6" />
    </div>
    <div className="flex-1 min-w-[220px]">
      <div className="font-display font-semibold text-lg">Add the Practice Tests pack</div>
      <div className="text-sm text-muted-foreground">
        One full-length attempt on each of the 4 subjects. Each additional attempt is {money(5)}.
      </div>
    </div>
    <div className="text-3xl font-display font-semibold" data-testid="ged-pack-price">{money(price)}</div>
    <Button
      className="h-11"
      onClick={async () => { setBusy("pack"); await checkout("pack"); setBusy(null); }}
      disabled={busy === "pack"}
      data-testid="ged-buy-pack"
    >
      {busy === "pack" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
      Add practice tests
    </Button>
  </div>
);

const SubjectButton = ({ subject, available, inProgress, credit, retestPrice, busy, setBusy, hasBase, unlimited, start }) => {
  const key = `start-${subject}`;
  const retestKey = `retest-${subject}`;
  if (!hasBase) {
    return (
      <Button variant="outline" className="bg-white" disabled data-testid={`ged-locked-${subject}`}>
        <Lock className="h-4 w-4 mr-2" />Unlock GED Prep first
      </Button>
    );
  }
  if (!available) {
    return <Button disabled data-testid={`ged-coming-soon-${subject}`}>Practice test coming soon</Button>;
  }
  if (unlimited || credit > 0 || inProgress) {
    return (
      <Button onClick={() => start(subject)} disabled={busy === key} data-testid={`ged-start-${subject}`}>
        {busy === key ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : inProgress ? <RotateCcw className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        {inProgress ? "Resume practice test" : unlimited ? "Start practice test" : credit > 1 ? `Start test · ${credit} attempts left` : "Start practice test"}
      </Button>
    );
  }
  // No credit left for this subject
  return (
    <Button
      onClick={async () => { setBusy(retestKey); await checkout("retest", { subject }); setBusy(null); }}
      disabled={busy === retestKey}
      data-testid={`ged-retest-${subject}`}
    >
      {busy === retestKey ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
      Retake for {money(retestPrice)}
    </Button>
  );
};

const PricingNote = ({ ent }) => (
  <div className="mt-6 bg-[#FBFAF4] border border-border rounded-[14px] p-4 text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-2" data-testid="ged-pricing-note">
    <span>GED Prep (12 months): <span className="text-[#071A22] font-medium">{money(ent.prices.base)}</span></span>
    <span>Practice Tests pack: <span className="text-[#071A22] font-medium">{money(ent.prices.pack)}</span></span>
    <span>Retake any subject: <span className="text-[#071A22] font-medium">{money(ent.prices.retest)}</span></span>
    {ent.base_expires_at && <span className="ml-auto inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Access through {new Date(ent.base_expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
  </div>
);

export default function GedHub() {
  const [r, setR] = useState(null);
  const [tests, setTests] = useState(null);
  const [ent, setEnt] = useState(null);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = () => Promise.all([api.get("/ged/readiness"), api.get("/ged/tests"), api.get("/ged/entitlement")])
    .then(([a, b, c]) => { setR(a.data); setTests(b.data); setEnt(c.data); })
    .catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(); }, []);

  const start = async (subject) => {
    setBusy(`start-${subject}`);
    try {
      const { data } = await api.post(`/ged/tests/${subject}/start`);
      navigate(`/ged/attempts/${data.attempt_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
      load();
    }
  };

  if (!r || !tests || !ent) return <Loading />;
  const tmap = Object.fromEntries(tests.map((t) => [t.subject, t]));
  const hasBase = ent.has_base;
  const totalCredits = ent.total_credits;

  return (
    <div className="max-w-[1200px]">
      <PageHeader
        eyebrow="GED prep"
        title={hasBase ? "Your GED readiness" : "GED Prep · an adult-learner track"}
        subtitle={hasBase
          ? `${r.ready_count} of 4 tests in the passing range (145+). ${r.note}`
          : "A separate program for adult learners aiming for the GED. Unlocks 4 standards-aligned courses, lessons, and the AI tutor. Practice tests are an optional add-on."}
      />

      <GedPrepDisclaimer className="mb-6" />

      {ent.comped && <CompedBanner ent={ent} />}
      {!hasBase && <Paywall price={ent.prices.base} busy={busy} setBusy={setBusy} />}
      {hasBase && !ent.unlimited && totalCredits === 0 && tests.every((t) => !t.in_progress) && (
        <PackCard price={ent.prices.pack} busy={busy} setBusy={setBusy} />
      )}

      <div className="grid md:grid-cols-2 gap-5" data-testid="ged-readiness-grid">
        {r.subjects.map((s) => {
          const Icon = ICONS[s.subject];
          const t = tmap[s.subject];
          const credit = (ent.credits && ent.credits[s.subject]) || 0;
          const inProgress = Boolean(t?.in_progress);
          return (
            <div key={s.subject} className="bg-white border border-border rounded-[18px] p-6 lift" data-testid={`ged-card-${s.subject}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#D7F3F1] text-[#0B6B6B] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-lg">{s.title}</div>
                  <span className={`text-xs rounded-full px-2 py-0.5 ${BAND_TONE[s.band.key]}`} data-testid={`ged-band-${s.subject}`}>{s.band.label}</span>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-display font-semibold" data-testid={`ged-estimate-${s.subject}`}>{s.estimate ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">est. score</div>
                </div>
              </div>
              <div className="mt-4"><ScoreMeter score={s.estimate} /><div className="flex justify-between text-[11px] text-muted-foreground mt-1"><span>100</span><span>145 pass</span><span>200</span></div></div>
              <div className="mt-3 text-xs text-muted-foreground">Based on {s.basis}.</div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Lessons mastered</div><div className="font-semibold">{s.mastered} / {s.lessons}</div></div>
                <div><div className="text-xs text-muted-foreground">Quiz average</div><div className="font-semibold">{s.avg_quiz != null ? `${s.avg_quiz}%` : "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Best practice</div><div className="font-semibold">{s.practice_best ?? "—"}</div></div>
              </div>
              <Progress value={s.lessons ? (s.mastered * 100) / s.lessons : 0} className="h-1.5 mt-3" />
              {(s.weak_topics.length > 0 || s.next_lessons.length > 0) && (
                <div className="mt-4 rounded-xl bg-[#FBFAF4] border border-border p-3 text-sm">
                  <div className="flex items-center gap-1 font-medium"><Target className="h-4 w-4 text-[#0B6B6B]" />Focus next</div>
                  <ul className="mt-1 space-y-0.5 text-muted-foreground">
                    {s.weak_topics.map((w) => <li key={w}>· {w}</li>)}
                    {s.weak_topics.length === 0 && s.next_lessons.map((l) => <li key={l.lesson_id}>· <Link to={`/lessons/${l.lesson_id}`} className="underline hover:text-[#0B6B6B]">{l.title}</Link></li>)}
                  </ul>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <SubjectButton
                  subject={s.subject}
                  available={!!t?.available}
                  inProgress={inProgress}
                  credit={credit}
                  retestPrice={ent.prices.retest}
                  busy={busy}
                  setBusy={setBusy}
                  hasBase={hasBase}
                  unlimited={ent.unlimited}
                  start={start}
                />
                {s.course_id && <Button variant="outline" className="bg-white" asChild><Link to={`/courses/${s.course_id}`} data-testid={`ged-course-${s.subject}`}><BookOpen className="h-4 w-4 mr-2" />Open course</Link></Button>}
              </div>
              {t && (
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Timer className="h-3.5 w-3.5" />{t.minutes} min · {t.questions} questions{t.essay ? " + essay" : ""}</span>
                  {hasBase && <span data-testid={`ged-credits-${s.subject}`}>{ent.unlimited ? "Unlimited attempts" : credit > 0 ? `${credit} attempt${credit > 1 ? "s" : ""} available` : "No attempts left"}</span>}
                </div>
              )}
              {t?.attempts?.length > 0 && (
                <div className="mt-4 border-t border-border pt-3" data-testid={`ged-history-${s.subject}`}>
                  <div className="text-xs text-muted-foreground mb-1">Past attempts</div>
                  {t.attempts.slice(0, 4).map((a) => (
                    <Link key={a.attempt_id} to={`/ged/attempts/${a.attempt_id}`} className="flex justify-between text-sm py-1 hover:text-[#0B6B6B]">
                      <span>{fmtDate(a.started_at)}</span><span className="font-medium">{a.result?.estimated_score} · {a.result?.percent}%</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!ent.comped && <PricingNote ent={ent} />}

      <div className="mt-6 bg-white border border-border rounded-[18px] p-5 sm:p-6 flex flex-wrap items-center gap-5" data-testid="ged-real-handoff-card">
        <div className="h-12 w-12 rounded-xl bg-[#D7F3F1] text-[#0B6B6B] flex items-center justify-center shrink-0"><ExternalLink className="h-6 w-6" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-semibold text-lg">Ready to take the real GED?</div>
          <div className="text-sm text-muted-foreground">We've prepared you here — the official test is scheduled through GED Testing Service. Walk through what to expect, costs, and how to register.</div>
        </div>
        <Button asChild variant="outline" className="bg-white" data-testid="ged-real-handoff-cta">
          <Link to="/ged/take-real-ged">See how it works</Link>
        </Button>
      </div>

      <p className="mt-6 text-xs text-muted-foreground max-w-3xl">
        GED® is a registered trademark of the American Council on Education. GED Testing Service is not affiliated with and does not endorse Scholar Campus. <span className="font-semibold text-[#071A22]">Scholar Campus provides GED prep and practice tests only — we do not administer the official GED exam or issue GED credentials.</span> Scores shown here are unofficial estimates; official scores come only from the GED test taken through GED Testing Service.
      </p>
    </div>
  );
}
